import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { registerDestructor } from '@ember/destroyable';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import Text from 'tio-ui/components/text';
import { PhCheckCircle, PhWarning } from 'ember-phosphor-icons';
import type { UploadFile, FileQueueService } from 'ember-file-upload';
import type ObservablesService from 'tio-common/services/observables';
import type { ObservableDocumentAttributes } from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import type { IntlService } from 'ember-intl';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type Owner from '@ember/owner';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
// PDF statements and plain-text NSLDS exports — both are in the backend's
// ObservableAsset::ALLOWED_CONTENT_TYPES. Drag-and-drop bypasses the file
// input's `accept` hint, so this list also drives handleFileAdded validation.
const VALID_EXTENSIONS = ['pdf', 'txt'];
const ACCEPTED_FILE_TYPES = VALID_EXTENSIONS.map((extension) => `.${extension}`).join(',');
const PROCESSING_TIMEOUT_MS = 60_000;

type UploadState = 'idle' | 'processing' | 'success' | 'error';

type BatchStatusAttributes = Partial<ObservableDocumentAttributes> & {
  extractionState?: ObservableDocumentModel['extractionState'];
  reflectionState?: ObservableDocumentModel['reflectionState'];
};

type SlrAlert = Pick<import('tio-employee/services/reimagined-data').SlrAlert, 'description'>;

type BatchStatus = {
  id: string;
  recordAttributes: BatchStatusAttributes;
  transition: { event: string };
  slrAlerts?: SlrAlert[];
};

/**
 * PageStateUploadCta — the "Add Loan Account" call-to-action on the
 * contribution-history page-state heroes (`no-accounts`, `waiting-period`).
 *
 * Replaces the old `LinkTo authenticated.observability.upload` CTA: uploads
 * must not route the user off the SLR dashboard, and the drawer pattern is
 * retired on this page, so this follows the inline pattern established by
 * LoanStandingAttentionCard — selecting a file IS the submission. The
 * document goes through the same observable-document batch pipeline as
 * IssueNotificationCard / the old drawer.
 */
interface PageStateUploadCtaSignature {
  Args: {
    /** Person whose documents are being uploaded */
    person: PersonModel;
    /**
     * Called once the uploaded document has been reflected server-side, i.e.
     * the point at which the dashboard model can return a different answer.
     * Callers MUST use this to refetch server-derived state (route model,
     * alert trackedFunctions) — nothing else re-issues those requests. Fires
     * even when reflection surfaced a new alert (the local error state): the
     * server's answer changed either way.
     */
    onUploadComplete?: () => void;
  };
  Element: HTMLDivElement;
}

export default class PageStateUploadCta extends Component<PageStateUploadCtaSignature> {
  @service declare observables: ObservablesService;
  @service declare cable: CableService;
  @service declare intl: IntlService;
  @service declare fileQueue: FileQueueService;

  @tracked state: UploadState = 'idle';
  @tracked errorMessage: string | null = null;
  @tracked processingTimedOut = false;

  // Queue identity is per component instance; the batch is re-minted per
  // submission so a retry after an error never reattaches to the old
  // batch-status stream.
  #instanceId = crypto.randomUUID();
  #batchId = crypto.randomUUID();
  #subscriptionParams: object | null = null;
  #processingTimer?: ReturnType<typeof setTimeout>;

  constructor(owner: Owner, args: PageStateUploadCtaSignature['Args']) {
    super(owner, args);
    registerDestructor(this, () => {
      this.#unsubscribe();
      clearTimeout(this.#processingTimer);
    });
  }

  get queueName() {
    return `slr-page-state-upload-${this.#instanceId}`;
  }

  get queue() {
    return this.fileQueue.findOrCreate(this.queueName);
  }

  #unsubscribe = () => {
    if (this.#subscriptionParams) {
      this.cable.unsubscribe(this.#subscriptionParams);
      this.#subscriptionParams = null;
    }
  };

  #settle = (state: UploadState, errorMessage: string | null = null) => {
    this.#unsubscribe();
    clearTimeout(this.#processingTimer);
    this.errorMessage = errorMessage;
    this.state = state;
  };

  #subscribeToBatchStatus = () => {
    const channelParams = {
      channel: 'ObservableDocumentBatchStatusChannel',
      batch: this.#batchId,
    };

    this.#subscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        const msg = data as BatchStatus;
        const { id, recordAttributes, transition } = msg;

        this.observables.updateObservableDocument({ ...recordAttributes, id });

        const extractionFailed = ['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(
          recordAttributes.extractionState ?? ''
        );

        if (extractionFailed) {
          const isInvalidDocument = recordAttributes.extractionState === 'ExtractionState.INVALID';
          this.#settle(
            'error',
            isInvalidDocument
              ? this.intl.t('observability.wizard.upload.error_invalid_document')
              : this.intl.t('observability.wizard.upload.error_failed')
          );
          return;
        }

        const reflectionSuccessful = transition.event === 'content_reflected';
        const reflectionFailed = transition.event === 'reflection_failed';

        if (reflectionSuccessful || reflectionFailed) {
          if (reflectionSuccessful) {
            // Check post-reflection SLR alerts from payment validation
            const [firstAlert] = msg.slrAlerts ?? [];
            if (firstAlert) {
              this.#settle(
                'error',
                firstAlert.description ?? this.intl.t('observability.wizard.upload.error_failed')
              );
            } else {
              this.#settle('success');
            }
            // Outside the firstAlert branch on purpose — see onUploadComplete.
            this.args.onUploadComplete?.();
          } else {
            this.#settle('error', this.intl.t('observability.wizard.upload.error_failed'));
          }
        }
      },
    });

    this.#processingTimer = setTimeout(() => {
      if (this.state === 'processing') {
        this.processingTimedOut = true;
      }
    }, PROCESSING_TIMEOUT_MS);
  };

  // Selecting a file IS the submission (the inline pattern from
  // LoanStandingAttentionCard — no second confirm step).
  handleFileAdded = async (uploadFile: UploadFile) => {
    // Full status reset — a new selection starts a new attempt, so a stale
    // success row must never render next to this attempt's outcome.
    this.state = 'idle';
    this.errorMessage = null;
    this.processingTimedOut = false;
    const file = uploadFile.file;

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!VALID_EXTENSIONS.includes(extension)) {
      this.errorMessage = this.intl.t('observability.wizard.upload.invalid_file_type', {
        formats: VALID_EXTENSIONS.map((validExtension) => validExtension.toUpperCase()).join(', '),
      });
      this.queue.remove(uploadFile);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.errorMessage = this.intl.t('observability.wizard.upload.file_too_large', {
        fileSize: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        maxSize: MAX_FILE_SIZE_MB,
      });
      this.queue.remove(uploadFile);
      return;
    }

    this.#unsubscribe();
    clearTimeout(this.#processingTimer);
    this.#batchId = crypto.randomUUID();
    this.state = 'processing';
    this.#subscribeToBatchStatus();

    try {
      await this.observables.createObservableDocument(uploadFile, {
        person: this.args.person,
        batch: this.#batchId,
      });
    } catch {
      this.#settle('error', this.intl.t('observability.wizard.upload.error_failed'));
    } finally {
      // The create chain consumed the file; release it so the queue can accept a retry.
      this.queue.remove(uploadFile);
    }
  };

  <template>
    <div class="tio-stack-start gap-2" data-test-slr-page-state-upload ...attributes>
      {{! role="status" / role="alert" so screen readers announce the state
          swaps after the (silent, sr-only-input) file selection. }}
      {{#if (eq this.state "processing")}}
        <div
          role="status"
          class="tio-row-tight w-full py-2"
          data-test-slr-page-state-upload-progress
        >
          <Text @size="sm" @weight="medium" class="tio-fg-secondary whitespace-nowrap">
            {{t "slr.reimagined.processing_message"}}
          </Text>
          <div class="flex-1 rounded-full processing-indicator"></div>
        </div>
        {{#if this.processingTimedOut}}
          <div
            role="status"
            class="tio-bg-warning-soft border tio-border-warning rounded-lg p-3 tio-row-top-tight w-full"
          >
            <PhWarning @size={{16}} class="tio-fg-warning shrink-0 mt-0.5" />
            <Text @variant="warning" @size="xs">
              {{t "observability.wizard.upload.processing_slow"}}
            </Text>
          </div>
        {{/if}}
      {{else}}
        {{#if (eq this.state "success")}}
          <div role="status" class="tio-row-tight" data-test-slr-page-state-upload-success>
            <PhCheckCircle @size={{20}} class="tio-fg-success shrink-0" @weight="fill" />
            <Text @size="sm" @weight="medium">
              {{t "slr.reimagined.successfully_added_loan_account"}}
            </Text>
          </div>
        {{/if}}
        {{#if this.errorMessage}}
          <div
            role="alert"
            class="tio-row-top-tight rounded-lg border tio-border-danger tio-bg-danger-soft p-3 w-full"
            data-test-slr-page-state-upload-error
          >
            <PhWarning @size={{16}} class="tio-fg-danger shrink-0 mt-0.5" />
            <Text @size="xs" class="tio-fg-danger-h">{{this.errorMessage}}</Text>
          </div>
        {{/if}}
        {{#let (fileQueue name=this.queueName onFileAdded=this.handleFileAdded) as |queue|}}
          {{! A label wrapping the (visually hidden, still focusable) file
              input keeps the exact .tio-slr-page-state-cta visuals of the
              LinkTo it replaces while the browser owns picker opening. }}
          <label class="tio-slr-page-state-cta cursor-pointer" data-test-slr-page-state-upload-cta>
            {{t "slr.reimagined.page_state_no_accounts_cta"}}
            <input
              {{queue.selectFile}}
              type="file"
              class="sr-only"
              accept={{ACCEPTED_FILE_TYPES}}
              data-test-slr-page-state-upload-input
            />
          </label>
        {{/let}}
        <Text @size="xs" class="tio-fg-muted">
          {{t "slr.reimagined.supported_formats_loan_document"}}
        </Text>
      {{/if}}
    </div>
  </template>
}
